import moment from 'moment';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { useAxios } from '../../utils/hook/useAxios';

const HOURS = Array.from({ length: 48 }, (_, i) => {
  const h = String(Math.floor(i / 2)).padStart(2, '0');
  const m = i % 2 === 0 ? '00' : '30';
  return `${h}:${m}`;
});

function slotLabel(slot) {
  const today = moment().startOf('day');
  const d = moment(slot.specificDate);
  const diff = d.diff(today, 'days');
  if (diff === 0) return "Aujourd'hui";
  if (diff === 1) return 'Demain';
  if (diff > 1 && diff < 7) return `Dans ${diff} jours`;
  return d.format('dddd D MMMM YYYY');
}

function groupByMonth(slots) {
  const map = {};
  for (const s of slots) {
    const key = moment(s.specificDate).format('MMMM YYYY');
    if (!map[key]) map[key] = [];
    map[key].push(s);
  }
  return map;
}

function CustomAvailabilitiesTab({ userId }) {
  const axios = useAxios();

  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPast, setShowPast] = useState(false);

  const [date, setDate] = useState('');
  const [start, setStart] = useState('08:00');
  const [end, setEnd] = useState('20:00');
  const [label, setLabel] = useState('');

  const formRef = useRef(null);

  const fetchSlots = async () => {
    try {
      const { data } = await axios.get(`/customavailabilities/${userId}`);
      setSlots(Array.isArray(data) ? data : []);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userId) fetchSlots();
  }, [userId]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!date) return toast.warning('Veuillez choisir une date');
    if (start >= end) return toast.warning("L'heure de fin doit être après l'heure de début");

    setSaving(true);
    try {
      await axios.post('/customavailabilities', {
        userId,
        specificDate: date,
        startTime: start,
        endTime: end,
        label: label.trim() || null,
      });
      toast.success('Créneau ajouté');
      setDate('');
      setLabel('');
      await fetchSlots();
    } catch (err) {
      toast.error(err.response?.data?.message || "Erreur lors de l'ajout");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`/customavailabilities/${id}`);
      setSlots((prev) => prev.filter((s) => s.id !== id));
      toast.success('Créneau supprimé');
    } catch {
      toast.error('Erreur lors de la suppression');
    }
  };

  const today = moment().format('YYYY-MM-DD');
  const upcoming = slots.filter((s) => s.specificDate >= today);
  const past = slots.filter((s) => s.specificDate < today);

  const upcomingGroups = groupByMonth(upcoming);

  return (
    <div>
      {/* ── Formulaire ajout ── */}
      <div className="card border-0 shadow-sm rounded-4 p-3 mb-4">
        <p className="fw-semibold mb-3" style={{ fontSize: 14 }}>
          <i className="bi bi-plus-circle me-2 text-primary"></i>
          Ajouter un créneau de disponibilité personnalisé
        </p>
        <form ref={formRef} onSubmit={handleAdd}>
          <div className="row g-2 align-items-end">
            <div className="col-12 col-sm-4">
              <label className="form-label small fw-semibold mb-1">Date</label>
              <input
                type="date"
                className="form-control form-control-sm"
                min={today}
                value={date}
                onChange={(e) => setDate(e.target.value)}
                required
              />
            </div>
            <div className="col-6 col-sm-2">
              <label className="form-label small fw-semibold mb-1">Début</label>
              <select
                className="form-select form-select-sm"
                value={start}
                onChange={(e) => setStart(e.target.value)}
              >
                {HOURS.map((h) => (
                  <option key={h} value={h}>
                    {h}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-6 col-sm-2">
              <label className="form-label small fw-semibold mb-1">Fin</label>
              <select
                className="form-select form-select-sm"
                value={end}
                onChange={(e) => setEnd(e.target.value)}
              >
                {HOURS.map((h) => (
                  <option key={h} value={h}>
                    {h}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-12 col-sm-3">
              <label className="form-label small fw-semibold mb-1">
                Libellé <span className="text-muted fw-normal">(optionnel)</span>
              </label>
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="ex: Fête nationale"
                value={label}
                maxLength={120}
                onChange={(e) => setLabel(e.target.value)}
              />
            </div>
            <div className="col-12 col-sm-1">
              <button
                type="submit"
                className="btn btn-primary btn-sm w-100"
                disabled={saving}
                style={{ minWidth: 80 }}
              >
                {saving ? (
                  <span className="spinner-border spinner-border-sm" />
                ) : (
                  <>
                    <i className="bi bi-check2-circle me-1"></i>Ajouter
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>

      {/* ── Explication ── */}
      <div className="alert alert-info py-2 px-3 mb-4" style={{ fontSize: 13 }}>
        <i className="bi bi-info-circle me-2"></i>
        Les créneaux personnalisés s'appliquent <strong>à des dates précises</strong> et complètent
        vos plages hebdomadaires. Utilisez-les pour des disponibilités exceptionnelles.
      </div>

      {/* ── Liste ── */}
      {loading ? (
        <div className="text-center py-4">
          <div className="spinner-border text-primary spinner-border-sm" />
        </div>
      ) : upcoming.length === 0 && past.length === 0 ? (
        <div className="text-center py-5 text-muted">
          <i className="bi bi-calendar-x" style={{ fontSize: 36 }}></i>
          <p className="mt-2 mb-0 small">Aucun créneau personnalisé pour l'instant</p>
        </div>
      ) : (
        <>
          {upcoming.length === 0 && (
            <p className="text-muted small text-center mb-3">Aucun créneau à venir</p>
          )}

          {Object.entries(upcomingGroups).map(([month, group]) => (
            <div key={month} className="mb-4">
              <p
                className="text-muted small fw-semibold text-uppercase mb-2"
                style={{ letterSpacing: '.5px' }}
              >
                {month}
              </p>
              <div className="d-flex flex-column gap-2">
                {group.map((slot) => (
                  <SlotCard key={slot.id} slot={slot} onDelete={handleDelete} isPast={false} />
                ))}
              </div>
            </div>
          ))}

          {past.length > 0 && (
            <div className="mt-2">
              <button
                className="btn btn-link btn-sm text-muted p-0 mb-3"
                onClick={() => setShowPast((v) => !v)}
              >
                <i className={`bi bi-chevron-${showPast ? 'up' : 'down'} me-1`}></i>
                {showPast ? 'Masquer' : 'Voir'} les créneaux passés ({past.length})
              </button>
              {showPast && (
                <div className="d-flex flex-column gap-2">
                  {past.map((slot) => (
                    <SlotCard key={slot.id} slot={slot} onDelete={handleDelete} isPast />
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function SlotCard({ slot, onDelete, isPast }) {
  return (
    <div
      className={`d-flex align-items-center justify-content-between rounded-3 px-3 py-2 border ${
        isPast ? 'bg-light opacity-60' : 'bg-white'
      }`}
      style={{ fontSize: 13 }}
    >
      <div className="d-flex align-items-center gap-3">
        <div
          className="rounded-3 d-flex flex-column align-items-center justify-content-center text-primary"
          style={{ width: 44, height: 44, background: '#f0f4ff', flexShrink: 0 }}
        >
          <span className="fw-bold" style={{ fontSize: 16, lineHeight: 1 }}>
            {moment(slot.specificDate).format('D')}
          </span>
          <span style={{ fontSize: 10, textTransform: 'uppercase' }}>
            {moment(slot.specificDate).format('MMM')}
          </span>
        </div>
        <div>
          <div className="fw-semibold">{slotLabel(slot)}</div>
          <div className="text-muted">
            <i className="bi bi-clock me-1"></i>
            {slot.startTime?.slice(0, 5)} — {slot.endTime?.slice(0, 5)}
          </div>
          {slot.label && (
            <span
              className="badge bg-secondary-subtle text-secondary rounded-pill mt-1"
              style={{ fontSize: 11 }}
            >
              {slot.label}
            </span>
          )}
        </div>
      </div>
      {!isPast && (
        <button
          className="btn btn-sm btn-outline-danger rounded-circle p-1"
          style={{ width: 30, height: 30, lineHeight: 1 }}
          onClick={() => onDelete(slot.id)}
          title="Supprimer ce créneau"
        >
          <i className="bi bi-trash3" style={{ fontSize: 12 }}></i>
        </button>
      )}
    </div>
  );
}

export default CustomAvailabilitiesTab;
